import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { COUNTRY_CODES } from "@redotech/locale/country-codes";
import { CURRENCIES, Currency } from "@redotech/money/currencies";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { WeightFormat } from "./outbound-label-enums";

export enum LengthUnit {
  INCH = "in",
  CENTIMETER = "cm",
}
export const LengthUnitSchema = createEnum(LengthUnit, {
  enumName: "LengthUnit",
});

export const LengthSchema = z.object({
  unit: LengthUnitSchema.meta({ privacy: privacy.nonPii }),
  value: z.number().meta({ privacy: privacy.nonPii }),
});

export type Length = z.infer<typeof LengthSchema>;

export const INCH_TO_CENTIMETER = 2.54;
export function inchesToCentimeters(length: number): number {
  return length * INCH_TO_CENTIMETER;
}

export function lengthUnitDisplayName(unit: LengthUnit): string {
  switch (unit) {
    case LengthUnit.INCH:
      return "inches";
    case LengthUnit.CENTIMETER:
      return "cm";
    default:
      return unit;
  }
}

export enum Precision {
  AT_LEAST = "at-least",
  EXACT = "exact",
}
export const PrecisionSchema = createEnum(Precision, { enumName: "Precision" });

export const CountryCodeSchema = z
  .string()
  .refine((code: any) => COUNTRY_CODES.includes(code), {
    message: "countryCode must be found in COUNTRY_CODES",
  });

export const CurrencySchema = z.custom<Currency>(
  (data: any) => CURRENCIES.includes(data),
  { message: "currencyCode must be found in CURRENCIES" },
);

export const MoneySchema = z.object({
  amount: z.string().meta({ privacy: privacy.nonPii }),
  currency: createEnum(Currency, { enumName: "Currency" }).meta({
    privacy: privacy.nonPii,
  }),
});

export type IMoney = z.infer<typeof MoneySchema>;

/** Build an IMoney from a decimal-string amount + currency code, validating the
 *  currency at the boundary. */
export function toMoney(amount: string, currency: string): IMoney {
  return MoneySchema.parse({ amount, currency });
}

export const WeightUnitSchema = z
  .custom<WeightUnit>((data: any) => !!weightTransforms[data], {
    message: "Unknown weight unit",
  })
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  .transform((arg) => weightTransforms[arg]!)
  .meta({ annotation: "String", mock: () => WeightUnit.G });

export const WeightSchema = z.object({
  unit: WeightUnitSchema.meta({ privacy: privacy.nonPii }),
  value: z.number().meta({ privacy: privacy.nonPii }),
});

export type Weight = z.infer<typeof WeightSchema>;

export interface IWeight extends z.ZodType<Weight> {}

const weightTransforms: Record<string, WeightUnit> = {
  GRAMS: WeightUnit.G,
  KILOGRAMS: WeightUnit.Kg,
  OUNCES: WeightUnit.Oz,
  POUNDS: WeightUnit.Lb,
  TONNES: WeightUnit.T,
  Gram: WeightUnit.G,
  Kilogram: WeightUnit.Kg,
  Ounce: WeightUnit.Oz,
  Pound: WeightUnit.Lb,
  Tonne: WeightUnit.T,
  g: WeightUnit.G,
  kg: WeightUnit.Kg,
  lb: WeightUnit.Lb,
  lbs: WeightUnit.Lb,
  oz: WeightUnit.Oz,
  t: WeightUnit.T,
  G: WeightUnit.G,
  KG: WeightUnit.Kg,
  LB: WeightUnit.Lb,
  LBS: WeightUnit.Lb,
  OZ: WeightUnit.Oz,
  T: WeightUnit.T,
};

const weightUnitConversion: Record<WeightUnit, Record<WeightUnit, number>> = {
  [WeightUnit.Oz]: {
    [WeightUnit.Lb]: 0.0625,
    [WeightUnit.Oz]: 1,
    [WeightUnit.G]: 28.349_523_125,
    [WeightUnit.Kg]: 0.028_349_523_1,
    [WeightUnit.T]: 0.000_028_349_5,
  },
  [WeightUnit.Lb]: {
    [WeightUnit.Oz]: 16,
    [WeightUnit.Lb]: 1,
    [WeightUnit.G]: 453.592_37,
    [WeightUnit.Kg]: 0.453_592_37,
    [WeightUnit.T]: 0.000_453_592_4,
  },
  [WeightUnit.G]: {
    [WeightUnit.Oz]: 0.035_273_961_9,
    [WeightUnit.Lb]: 0.002_204_622_6,
    [WeightUnit.G]: 1,
    [WeightUnit.Kg]: 0.001,
    [WeightUnit.T]: 0.001,
  },
  [WeightUnit.Kg]: {
    [WeightUnit.Oz]: 35.273_961_949_6,
    [WeightUnit.Lb]: 2.204_622_621_8,
    [WeightUnit.G]: 1000,
    [WeightUnit.Kg]: 1,
    [WeightUnit.T]: 0.001,
  },
  [WeightUnit.T]: {
    [WeightUnit.Oz]: 35_273.961_95,
    [WeightUnit.Lb]: 2204.622_621_8,
    [WeightUnit.G]: 1_000_000,
    [WeightUnit.Kg]: 1000,
    [WeightUnit.T]: 1,
  },
};

export function convertWeight(weight: Weight, to: WeightUnit): Weight {
  const converted = weightUnitConversion[weight.unit][to] * weight.value;
  return { unit: to, value: roundIfVeryClose(converted) };
}

function roundIfVeryClose(value: number): number {
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 0.001) {
    return rounded;
  }
  return value;
}

export const roundWeight = (weight: number) => {
  return Math.round(weight * 100) / 100;
};

export const splitWeight = (
  weight: Weight,
  units: [WeightUnit, WeightUnit],
): [number, number] => {
  const firstPart = convertWeight(weight, units[0]);
  let firstWhole = Math.floor(firstPart.value);
  const secondPart = convertWeight(
    { value: firstPart.value - firstWhole, unit: units[0] },
    units[1],
  );
  let secondWhole = Math.ceil(secondPart.value);
  // Due to rounding errors, rounding up the second unit
  // may make it a whole number of the first unit. (1 lb 16 oz)
  // Account for that here. (2 lb 0 oz)
  if (
    convertWeight({ value: secondWhole, unit: units[1] }, units[0]).value === 1
  ) {
    firstWhole++;
    secondWhole = 0;
  }
  return [firstWhole, secondWhole];
};

export function formatWeight(
  weight: Weight,
  weightFormat: WeightFormat,
): string {
  const parts: string[] = [];
  if (weightFormat === WeightFormat.METRIC) {
    const [kg, g] = splitWeight(weight, [WeightUnit.Kg, WeightUnit.G]);
    if (kg) {
      parts.push(`${kg} kg`);
    }
    if (g) {
      parts.push(`${g} g`);
    }
  } else {
    const [lbs, oz] = splitWeight(weight, [WeightUnit.Lb, WeightUnit.Oz]);
    if (lbs === 1) {
      parts.push(`${lbs} lb`);
    } else if (lbs > 1) {
      parts.push(`${lbs} lbs`);
    }
    if (oz) {
      parts.push(`${oz} oz`);
    }
  }
  if (parts.length === 0) {
    return weightFormat === WeightFormat.METRIC ? "0 g" : "0 oz";
  }
  return parts.join(", ");
}

export function addWeight(weight: Weight, weightToAdd: Weight): Weight {
  const convertedWeightToAdd = convertWeight(weightToAdd, weight.unit);
  return {
    unit: weight.unit,
    value: weight.value + convertedWeightToAdd.value,
  };
}

export function weightsEqual(
  weight1: Weight | undefined | null,
  weight2: Weight | undefined | null,
): boolean {
  // If both weights are undefined or null, they are equal
  if (!weight1 && !weight2) {
    return true;
  }
  // If we have one weight and the other is undefined or null, they are not equal
  if (!weight1 || !weight2) {
    return false;
  }
  // Otherwise convert to grams and compare
  const convertedWeight1 = convertWeight(weight1, WeightUnit.G);
  const convertedWeight2 = convertWeight(weight2, WeightUnit.G);
  return convertedWeight1.value === convertedWeight2.value;
}
